// src/components/PublicLayout.js

import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  CssBaseline,
  Box,
  IconButton,
  Drawer,
  Typography,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { Link, Outlet, Navigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase';

const LogoMark = ({ dark = false }) => (
  <Box
    component={Link}
    to="/"
    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none' }}
  >
    <Typography
      component="span"
      sx={{
        fontFamily: '"Plus Jakarta Sans", sans-serif',
        fontWeight: 800,
        fontSize: '1.25rem',
        color: dark ? '#FDFAF5' : '#1A1410',
        lineHeight: 1,
        transition: 'color 0.3s',
      }}
    >
      List
    </Typography>
    <Typography
      component="span"
      sx={{
        fontFamily: '"Fraunces", serif',
        fontStyle: 'italic',
        fontWeight: 700,
        fontSize: '1.25rem',
        color: '#E8891A',
        lineHeight: 1,
      }}
    >
      Link
    </Typography>
  </Box>
);

const navLinks = [
  { label: 'Features', to: '/learn-more' },
  { label: 'Blog', to: '/blog' },
];

function PublicLayout() {
  const [user, loading] = useAuthState(auth);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  console.log('PublicLayout state', { loading, hasUser: !!user, verified: user?.emailVerified });

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: '#FDFAF5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      />
    );
  }

  if (user && user.emailVerified) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div>
      <CssBaseline />

      {/* Fixed Navbar */}
      <AppBar
        position="fixed"
        sx={{
          background: scrolled ? 'rgba(253,250,245,0.88)' : 'transparent',
          backdropFilter: scrolled ? 'blur(12px) saturate(180%)' : 'none',
          borderBottom: scrolled
            ? '1px solid rgba(201,192,179,0.3)'
            : '1px solid transparent',
          boxShadow: scrolled ? '0 1px 20px rgba(26,20,16,0.06)' : 'none',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          height: scrolled ? 64 : 72,
        }}
      >
        <Toolbar
          sx={{
            height: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: { xs: 2, md: 4 },
            maxWidth: 1200,
            mx: 'auto',
            width: '100%',
          }}
        >
          <LogoMark />

          {/* Desktop nav */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: 1,
            }}
          >
            {navLinks.map(({ label, to }) => (
              <Button
                key={label}
                component={Link}
                to={to}
                sx={{
                  color: '#5C4F3D',
                  fontWeight: 600,
                  fontFamily: '"Plus Jakarta Sans", sans-serif',
                  fontSize: '0.9rem',
                  px: 2,
                  borderRadius: 100,
                  '&:hover': {
                    color: '#E8891A',
                    background: 'rgba(232,137,26,0.06)',
                  },
                  transition: 'color 0.2s, background 0.2s',
                }}
              >
                {label}
              </Button>
            ))}

            <Button
              variant="outlined"
              component={Link}
              to="/login"
              sx={{
                ml: 1,
                borderColor: '#E8891A',
                borderWidth: '1.5px',
                color: '#E8891A',
                fontWeight: 700,
                borderRadius: 100,
                px: 2.5,
                '&:hover': {
                  borderWidth: '1.5px',
                  borderColor: '#C46F0C',
                  backgroundColor: 'rgba(232,137,26,0.06)',
                },
              }}
            >
              Sign In
            </Button>

            <Button
              variant="contained"
              component={Link}
              to="/register"
              size="medium"
              sx={{ ml: 0.5, px: 3 }}
            >
              Get Started
            </Button>
          </Box>

          {/* Mobile hamburger */}
          <IconButton
            onClick={() => setMobileOpen(true)}
            sx={{
              display: { xs: 'flex', md: 'none' },
              color: '#1A1410',
            }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: {
            width: '80vw',
            maxWidth: 320,
            background: '#FDFAF5',
            px: 3,
            py: 3,
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <LogoMark />
          <IconButton
            onClick={() => setMobileOpen(false)}
            sx={{ color: '#5C4F3D' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 4 }}>
          {navLinks.map(({ label, to }) => (
            <Button
              key={label}
              component={Link}
              to={to}
              onClick={() => setMobileOpen(false)}
              sx={{
                justifyContent: 'flex-start',
                color: '#1A1410',
                fontFamily: '"Fraunces", serif',
                fontWeight: 600,
                fontSize: '1.3rem',
                px: 1,
                py: 1.5,
                borderRadius: 2,
                '&:hover': {
                  color: '#E8891A',
                  background: 'rgba(232,137,26,0.06)',
                },
              }}
            >
              {label}
            </Button>
          ))}
        </Box>

        <Divider sx={{ borderColor: '#EDE8DF', mb: 4 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Button
            variant="outlined"
            component={Link}
            to="/login"
            fullWidth
            onClick={() => setMobileOpen(false)}
            size="large"
          >
            Sign In
          </Button>
          <Button
            variant="contained"
            component={Link}
            to="/register"
            fullWidth
            onClick={() => setMobileOpen(false)}
            size="large"
          >
            Get Started Free
          </Button>
        </Box>
      </Drawer>

      {/* Spacer to prevent content hiding behind fixed AppBar */}
      <Box sx={{ height: { xs: 72, md: 72 } }} />

      {/* Main content */}
      <main>
        <Outlet />
      </main>
    </div>
  );
}

export default PublicLayout;
